import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { useTenantHost } from "app/tenant/TenantHostProvider";
import { buildPublicHospitalProfile } from "./publicHospitalProfileModel";

export default function PublicHospitalProfile() {
  const tenant = useTenantHost();
  const theme = useTheme();
  const profile = buildPublicHospitalProfile(tenant);
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    setLogoFailed(false);
  }, [profile.logoUrl]);

  useEffect(() => {
    if (!profile.hospitalName) return undefined;
    const previous = document.title;
    document.title = profile.hospitalName;
    return () => {
      document.title = previous;
    };
  }, [profile.hospitalName]);

  const showLogo = Boolean(profile.logoUrl) && !logoFailed;
  const accent = theme.palette.primary.main;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F8FAFC",
        color: "#0F172A",
      }}
    >
      <Box
        component="header"
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          px: { xs: 2, sm: 3, md: 5 },
          py: { xs: 2, md: 2.5 },
          borderBottom: "1px solid #E2E8F0",
          backgroundColor: "#FFFFFF",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          {showLogo ? (
            <Box
              component="img"
              src={profile.logoUrl}
              alt=""
              onError={() => setLogoFailed(true)}
              sx={{ width: 40, height: 40, objectFit: "contain", flexShrink: 0 }}
            />
          ) : null}
          {profile.hospitalName ? (
            <Typography
              component="p"
              sx={{
                m: 0,
                fontWeight: 600,
                fontSize: { xs: "1rem", md: "1.05rem" },
                lineHeight: 1.3,
                overflowWrap: "anywhere",
              }}
            >
              {profile.hospitalName}
            </Typography>
          ) : null}
        </Stack>
        <Button
          component={RouterLink}
          to="/session/signin"
          variant="contained"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 2.5,
            alignSelf: { xs: "stretch", sm: "center" },
            backgroundColor: accent,
            "&:hover": { backgroundColor: accent, filter: "brightness(0.92)" },
          }}
        >
          Staff sign in
        </Button>
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2, sm: 4 },
          py: { xs: 6, md: 10 },
        }}
      >
        <Stack
          spacing={{ xs: 3, md: 4 }}
          alignItems="center"
          sx={{ width: "100%", maxWidth: 640, textAlign: "center" }}
        >
          {showLogo ? (
            <Box
              sx={{
                width: "100%",
                maxWidth: { xs: 280, sm: 360 },
                borderRadius: 3,
                px: { xs: 3, md: 5 },
                py: { xs: 4, md: 6 },
                backgroundColor: accent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={profile.logoUrl}
                alt={profile.hospitalName ? `${profile.hospitalName} logo` : "Hospital logo"}
                onError={() => setLogoFailed(true)}
                sx={{ width: "100%", maxWidth: 280, height: "auto", objectFit: "contain" }}
              />
            </Box>
          ) : null}
          {profile.hospitalName ? (
            <Typography
              component="h1"
              sx={{
                m: 0,
                fontFamily: "Georgia, serif",
                fontWeight: 500,
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                lineHeight: 1.15,
                overflowWrap: "anywhere",
              }}
            >
              {profile.hospitalName}
            </Typography>
          ) : null}
          {profile.addressLines ? (
            <Typography
              component="address"
              sx={{
                m: 0,
                fontStyle: "normal",
                color: "#475569",
                fontSize: { xs: "0.95rem", sm: "1.05rem" },
                lineHeight: 1.55,
                maxWidth: 480,
                px: { xs: 1, sm: 0 },
                overflowWrap: "anywhere",
              }}
            >
              {profile.addressLines.map((line) => (
                <Box key={line} component="span" sx={{ display: "block" }}>
                  {line}
                </Box>
              ))}
            </Typography>
          ) : null}
        </Stack>
      </Box>

      <Box
        component="footer"
        sx={{
          px: { xs: 2, md: 5 },
          py: 2.5,
          textAlign: "center",
          borderTop: "1px solid #E2E8F0",
        }}
      >
        <Typography component="p" sx={{ m: 0, color: "#64748B", fontSize: "0.875rem" }}>
          Powered by Cheritto Health Solutions
        </Typography>
      </Box>
    </Box>
  );
}
